/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  STANDBY: 'standby',
  START: 'start',
  STOP: 'stop',
  ERROR: 'error',
  CONNECT_FAILED: 'connectionFailed'
};

export default {
  name: 'requirement121',
  data() {
    return {
      phase: PHASE.STANDBY,
      isLoading: false,
      systemId: '',
      modal_requirement_121: false
    };
  },
  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.CONNECT_FAILED:
          return 'danger';
        case PHASE.STOP:
          return 'success';
        case PHASE.START:
        case PHASE.STANDBY:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_121 = true;
    },

    /** 待機画面で次へが押下されたとき */
    clickConfirm() {
      this.phase = PHASE.START;
    },

    /**
     * セッション遷移を伴うデータ書込み
     */
    async write() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          // POST リクエストの body を指定する
          {
            id_token: token.id_token,
            type: 'write'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        // response が存在する場合、正常判定を行う(存在しない場合はエラー)
        if (response) {
          // エラーコールバックが呼ばれず、completed / failed が返ってきてポーリングが終了した時のみ、最終的な GET response が返ってくる。
          // エラーコールバックが呼ばれた場合は、null になる。
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 停止画面に遷移
              this.phase = PHASE.STOP;
              break;
            case config.STATUS_WSP_FAILED:
              // NonDefaultSession遷移で異常終了の場合、DefaultSession遷移を実行せずエラー画面に遷移
              switch (response.data.async_code) {
                case error.WSP_INITIALIZE_FAILED:
                  this.phase = PHASE.ERROR;
                  break;
                default:
                  await this.showCommonError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * エラー表示処理(DefaultSession遷移の結果に関わらずエラー表示)
     */
    async showCommonError() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // 実行結果に関わらずエラー画面を表示
        this.phase = PHASE.ERROR;
      } finally {
        this.isLoading = false;
      }
    },

    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_121 = false;
    },

    /**
     * 終了処理(エラー遷移なし)
     */
    async exit() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // エラー遷移なし：機能終了
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * その他エラー発生時の表示
     */
    showUnexpectedError() {
      // エラー画面に遷移する
      this.phase = PHASE.CONNECT_FAILED;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result handler.validate で返ってきたバリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },
  },

  mounted() {
    this.init();
  }
};